/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  findLatestPricing5PlanId,
  PRICING_5_X_EARLY_STAGE_SOLUTION_ID,
  percentDiscountForSolution,
} from 'embercom/lib/billing';
import type CardlessTrialService from 'embercom/services/cardless-trial-service';
import type QuoteService from 'embercom/services/quote-service';
import type ModalService from 'embercom/services/modal-service';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';

export default class CardlessConversionModalService extends Service {
  @tracked showModal = false;
  @tracked modalDismissedWhenTrialExpiredNonStorage = false;
  @tracked afterCloseModal: (() => void) | undefined;
  @tracked customerWantsToExtendTrial = false;

  @service declare quoteService: QuoteService;
  @service declare cardlessTrialService: CardlessTrialService;
  @service declare appService: AppService;
  @service declare experimentOptInService: any;
  @service declare customerService: any;
  @service declare modalService: ModalService;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare notificationsService: any;

  @action
  setCustomerWantsToExtendTrial(value: boolean) {
    this.customerWantsToExtendTrial = value;
  }

  async toggle() {
    try {
      await this.aiAgentSetupService.reloadWorkflowsCounts();
    } catch (e) {
      console.error(e);
    }

    this.modalService.openModal(
      'react/cardless-conversion-modal-pricing5',
      {
        onOpenChange: () => {
          this.modalService.closeModal();
        },
        activePlanId: findLatestPricing5PlanId(
          this.cardlessTrialService.activeOrExpiredPlanTrialIds,
        ),
        oneDollarTrialExtension: this.showExtendedTrialState,
        selfServe:
          !this.customerService.customer.inSalesProspectTrial &&
          this.appService.app.isNotSalesforceContracted,
        canUseEarlyStageDetection: this.canUseEarlyStageDetection,
        canUseProductSpecialistCTAs: this.canUseProductSpecialistCTAs,
        earlyStageDiscount: this.earlyStageDiscount,
        liteSeatCount: this.liteSeatCount,
        pauseWorkflowsWarningProps: this.pauseWorkflowsWarningProps,
      },
      {
        showIntercomWidget: true,
      },
    );
  }

  get customerCanExtendTrial() {
    return !!(
      (this.customerService.customer.inCardlessTrial ||
        this.customerService.customer.inExpiredCardlessTrial) &&
      !this.customerService.customer.inTrialExtension &&
      !this.customerService.customer.inExpiredTrialExtension
    );
  }

  get showExtendedTrialState() {
    return !!(
      this.customerCanExtendTrial &&
      this.customerWantsToExtendTrial &&
      this.appService.app?.teamPurchaseExperienceTrialExtensionsVariant
    );
  }

  get canUseProductSpecialistCTAs() {
    return this.cardlessTrialService.canUseProductSpecialistCTAs;
  }

  get canUseEarlyStageDetection() {
    return this.cardlessTrialService.canUseEarlyStageDetection;
  }

  get earlyStageDiscount() {
    return percentDiscountForSolution(PRICING_5_X_EARLY_STAGE_SOLUTION_ID);
  }

  get liteSeatCount() {
    return this.appService.app.humanAdminsWithLiteSeat.length;
  }

  get hasFinWorkflows() {
    return (
      this.aiAgentSetupService.finStats.workflowsCount.liveFinWorkflowsCount > 0 ||
      this.aiAgentSetupService.totalPausedFinWorkflowsCount > 0
    );
  }

  get hasLiveWorkflows() {
    return this.aiAgentSetupService.totalLiveWorkflowsCount > 0;
  }

  get numberOfWorkflows() {
    return this.hasLiveWorkflows
      ? this.aiAgentSetupService.totalLiveWorkflowsCount
      : this.aiAgentSetupService.totalPausedWorkflowsCount;
  }

  get workflowPauseVariant() {
    return this.hasLiveWorkflows ? 'will_be_paused' : 'will_remain_paused';
  }

  get pauseWorkflowsWarningProps() {
    return {
      numberOfWorkflows: this.numberOfWorkflows,
      hasFinWorkflows: this.hasFinWorkflows,
      workflowPauseVariant: this.workflowPauseVariant,
    };
  }
}

declare module '@ember/service' {
  interface Registry {
    cardlessConversionModalService: CardlessConversionModalService;
    'cardless-conversion-modal-service': CardlessConversionModalService;
  }
}
